/**
 * The declared-id universe (D226 split, from `reference-walkers.ts` at the
 * 500-line cap): what one pack declares, by family, precomputed once per
 * validation for every walk that checks a reference against it.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, CounterDefinition } from '@orpheus/content-schema';

import { type RecordUniverse, recordUniverseOf } from './record-validation.js';

/** The declared-id universe one pack establishes, precomputed once per validation. */
export interface DeclaredIdSets {
	/** D232: declared actor ids, for the two actor conditions and an id-shaped topic key. */
	readonly actorIds: ReadonlySet<string>;
	/**
	 * D226 (E19): the indexes of the triggers that listen to a COMMAND —
	 * `on.type` of `command_executed` or `command_rejected` — one of the
	 * places a command is in hand, where `direct_object_is`,
	 * `indirect_object_is` and `tool_is` (D229) may stand. A state watcher's
	 * index is never here.
	 */
	readonly commandListeningTriggerIndexes: ReadonlySet<number>;
	/** D244: the declared counters by id — a reference names one, and a threshold or a setting is judged against its range. */
	readonly counters: ReadonlyMap<string, CounterDefinition>;
	/** D239: the items hidden inside another (`containedIn.concealed`), for `item_found`. */
	readonly concealedItemIds: ReadonlySet<string>;
	/** D152: the items declaring `durability`, for `item_spent`. */
	readonly durableItemIds: ReadonlySet<string>;
	/** R11: declared ending ids, for `set_ending` and `ending_selected`. */
	readonly endingIds: ReadonlySet<string>;
	/** R12: declared log ids (each authored on its carrier item), for `log_read`. */
	readonly logIds: ReadonlySet<string>;
	/** R9: declared event ids, for the scheduling effects. */
	readonly eventIds: ReadonlySet<string>;
	readonly exitIds: ReadonlySet<string>;
	readonly flagIds: ReadonlySet<string>;
	readonly itemIds: ReadonlySet<string>;
	/** D244: the player's whole health (`manifest.player.health`), for a heal and its threshold read; `undefined` when the player has no body. */
	readonly playerHealth: number | undefined;
	/** D235: declared prompt ids (each authored on its actor), for `ask_prompt` and a `leadsTo`. */
	readonly promptIds: ReadonlySet<string>;
	/** D241: the declared Record — what `record_holds` is judged against; `undefined` when the pack keeps none. */
	readonly record: RecordUniverse | undefined;
	readonly roomIds: ReadonlySet<string>;
	/** R12: declared voice-line ids, for `speak_voice_line` and `voice_line_spoken`. */
	readonly voiceLineIds: ReadonlySet<string>;
}

/**
 * The declared-id universe, gathered from a pack — for the validators that
 * walk one layer's conditions and effects and want the plain sets.
 *
 * @remarks
 * Three validators (sprites, audio, equipment) each carried these lines as
 * "eight one-liners, uncoupled" — a stated trade, and at three copies the
 * DRY contract's mandate to extract. The semantic validator still builds its
 * own, because its collection also REPORTS duplicates; this one only reads.
 *
 * @param contentPack - The parsed pack
 * @returns Every declared id, by kind
 */
export const declaredIdSetsOf = (contentPack: ContentPackDefinition): DeclaredIdSets => ({
	actorIds: new Set(contentPack.actors.map((actor) => actor.actorId)),
	commandListeningTriggerIndexes: commandListeningTriggerIndexesOf(contentPack),
	concealedItemIds: new Set(contentPack.items.flatMap((item) => (item.containedIn?.concealed === true ? [item.itemId] : []))),
	counters: new Map(contentPack.counters.map((counter) => [counter.counterId as string, counter])),
	durableItemIds: new Set(contentPack.items.flatMap((item) => (item.durability === undefined ? [] : [item.itemId]))),
	endingIds: new Set(contentPack.endings.map((ending) => ending.endingId)),
	eventIds: new Set(contentPack.events.map((event) => event.eventId)),
	exitIds: new Set(contentPack.rooms.flatMap((room) => room.exits.map((exit) => exit.exitId))),
	flagIds: new Set(contentPack.flags.map((flag) => flag.flagId)),
	itemIds: new Set(contentPack.items.map((item) => item.itemId)),
	logIds: new Set(contentPack.items.flatMap((item) => (item.log === undefined ? [] : [item.log.logId]))),
	playerHealth: contentPack.manifest.player?.health,
	promptIds: new Set(contentPack.actors.flatMap((actor) => (actor.prompts ?? []).map((prompt) => prompt.promptId))),
	record: recordUniverseOf(contentPack),
	roomIds: new Set(contentPack.rooms.map((room) => room.roomId)),
	voiceLineIds: new Set(contentPack.voiceLines.map((voiceLine) => voiceLine.voiceLineId)),
});

/**
 * D226 (E19): which triggers have a command in hand — those listening for an
 * occurrence a command raises. Shared by both builders of the declared-id
 * universe, so the two cannot disagree about where the sentence may be read.
 *
 * @param contentPack - The parsed pack
 * @returns The indexes, in declaration order
 */
export const commandListeningTriggerIndexesOf = (contentPack: ContentPackDefinition): ReadonlySet<number> =>
	new Set(
		contentPack.triggers.flatMap((trigger, triggerIndex) =>
			trigger.on?.type === 'command_executed' || trigger.on?.type === 'command_rejected' ? [triggerIndex] : [],
		),
	);
